import {
  SPREADSHEET_PREVIEW_MAX_COLS,
  SPREADSHEET_PREVIEW_MAX_ROWS,
} from "@frontend-shared/constants";

// NOTE: Office MIME types the chat renders as an inline PDF thumbnail (Gotenberg
// converts them to PDF server-side). Word/PowerPoint only — spreadsheets are
// handled separately by SPREADSHEET_PREVIEW_MIME_TYPES below (rendered as a
// client-side grid, not a PDF). Kept in sync with the server allow-list
// apps/nsai/server/src/chat/constant/file-preview.constants.ts.
export const OFFICE_PREVIEW_MIME_TYPES: readonly string[] = [
  // NOTE: Modern OOXML documents (.docx/.pptx)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // NOTE: Legacy binary documents (.doc/.ppt)
  "application/msword",
  "application/vnd.ms-powerpoint",
];

// NOTE: Assistant-generated HTML documents (NSAI-3933). Previewed by rendering
// the original blob in a sandboxed iframe rather than converting to a PDF. The
// value itself lives in frontend-shared so every client agrees on what a
// previewable page is (NSAI-5656).
export { HTML_PREVIEW_MIME_TYPE } from "@frontend-shared/lib/html-preview";

// NOTE: Spreadsheet MIME types rendered client-side as an HTML grid
// (SpreadsheetTableViewer + SheetJS) directly from the original file blob, so
// they look like a real sheet instead of a paginated Gotenberg→PDF document
// (NSAI-3980). Legacy binary .xls (application/vnd.ms-excel) is intentionally
// excluded for now. Kept in sync with the Generated Files list
// apps/nsai/web/src/pages/generated-files/utils/get-preview-source.ts.
export const SPREADSHEET_PREVIEW_MIME_TYPES: readonly string[] = [
  // NOTE: CSV (the format the app currently generates for tabular data)
  "text/csv",
  // NOTE: Modern OOXML spreadsheet (.xlsx)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// NOTE: Spreadsheet MIME types the SpreadsheetTableViewer reads as delimited
// text (via blob.text()) rather than as a binary workbook (blob.arrayBuffer()).
export const SPREADSHEET_CSV_MIME_TYPES: ReadonlySet<string> = new Set([
  "text/csv",
]);

// NOTE: The spreadsheet grid is padded with empty cells so it fills the visible
// preview area (no white gap to the right of / below the data), and capped so a
// pathologically large workbook can't blow up the DOM. MIN_* are the floor when
// the viewport hasn't been measured yet or the data/viewport is small.
export const SPREADSHEET_MIN_ROWS = 40;
export const SPREADSHEET_MIN_COLS = 26;
// NOTE: NSAI-4326 — the caps now come from `frontend-shared` so this grid and
// the mobile CSV grid cannot drift apart. They were duplicate literals that
// happened to agree.
export const SPREADSHEET_MAX_ROWS = SPREADSHEET_PREVIEW_MAX_ROWS;
export const SPREADSHEET_MAX_COLS = SPREADSHEET_PREVIEW_MAX_COLS;

// NOTE: Pixel dimensions of a spreadsheet cell, used to compute how many empty
// rows/columns are needed to fill the viewport. Must track the Tailwind classes
// in SpreadsheetTableViewer — w-36 (9rem = 144px), w-12 gutter (3rem = 48px),
// and the row/header height from py-1.5 + text-sm line-height + 1px border
// (~33px).
export const SPREADSHEET_CELL_WIDTH_PX = 144;
export const SPREADSHEET_GUTTER_WIDTH_PX = 48;
export const SPREADSHEET_ROW_HEIGHT_PX = 33;
export const SPREADSHEET_HEADER_HEIGHT_PX = 33;

export const PDF_THUMBNAIL_WIDTH = 120;
export const PDF_MAIN_PAGE_SCALE = 1.5;

// NOTE: NSAI-6113 — how far outside the visible scroll area a PDF page still
// holds a rendered canvas: one viewport above and one below. Pages past it are
// released (backing store zeroed), so the canvases alive at once are bounded by
// the viewport rather than by the document's page count.
export const PDF_PREVIEW_RENDER_ROOT_MARGIN = "100% 0px";

// NOTE: Render width (px) for the inline first-page thumbnail shown in a chat
// message. Larger than the sidebar thumbnail so it stays crisp in the ~240px
// preview box on high-DPI screens.
export const PDF_INLINE_THUMBNAIL_WIDTH = 480;

// NOTE: NSAI-4045 — react-query cache keys for document previews, keyed by
// fileUuid (NOT by URL: the same file is reachable via the chat, analytics and
// shared-chat endpoints, and the analytics URL varies by ?scope/?projectUuid).
// PREVIEW_BLOB_QUERY_KEY holds the raw render blob (converted PDF for Office
// files, the original bytes for PDFs) shared by the document thumbnail and the
// full preview modal. DOCUMENT_THUMBNAIL_QUERY_KEY holds the small derived
// artifact (rasterized data URL), so a remount skips both the fetch and the
// pdf.js work. Spreadsheets render a static file-type icon card (NSAI-4641), so
// they fetch nothing until the modal opens.
export const PREVIEW_BLOB_QUERY_KEY = "chat-file-preview-blob";

// NOTE: NSAI-6621 — react-query key for a generated video's bytes, partitioned
// by cache scope exactly like the preview blobs above.
export const GENERATED_VIDEO_BLOB_QUERY_KEY = "chat-generated-video-blob";
export const DOCUMENT_THUMBNAIL_QUERY_KEY = "chat-document-thumbnail";

// NOTE: NSAI-4045 — the same fileUuid is reachable through endpoints with
// different authorization models (owner-only chat, ALL_MEMBERS_ANALYTICS_READ
// analytics, public share token). The cache key is partitioned by this scope so
// bytes fetched under one surface's permissions can never satisfy a read on
// another — a cache entry must never span authorization models.
export enum EPreviewCacheScope {
  OWNER = "owner",
  ANALYTICS = "analytics",
  SHARED = "shared",
  // NOTE: NSAI-4251 — the admin panel reaches any project's files through the
  // admin JWT + CHATS_READ, which is an authorization model of its own, so its
  // bytes get their own partition instead of borrowing the shared-link one.
  ADMIN = "admin",
}

// NOTE: A shared-chat view must re-validate access with the server on every
// render: its endpoint deliberately sends `no-store` so a revoked link stops
// working immediately (NSAI-3374). Caching those bytes would paint a revoked
// document from memory, so that scope keeps zero-length caching — i.e. exactly
// the behavior it has today, while authenticated surfaces get the reuse.
export const isCacheableScope = (scope: EPreviewCacheScope): boolean =>
  scope !== EPreviewCacheScope.SHARED;

// NOTE: Document thumbnails defer their download + rasterization until the card
// nears the viewport, so a long chat history never pays that cost up front.
export const DOCUMENT_THUMBNAIL_ROOT_MARGIN = "400px";

// NOTE: NSAI-4624 — the preview retry budget lives in frontend-shared so this
// thumbnail and the mobile preview modal cannot drift into different recovery
// behaviour. Re-exported here (like HTML_PREVIEW_MIME_TYPE above) so call sites
// keep importing their preview constants from one place.
export { PREVIEW_MAX_RETRY_ATTEMPTS } from "@frontend-shared/constants";

// NOTE: The derived thumbnail artifact (rasterized data URL) is small, so it
// outlives the default 5-minute gcTime — a chat reopened within half an hour
// paints its cards instantly without re-downloading the source blobs (which
// keep the default gcTime and stay memory-bounded).
export const PREVIEW_THUMBNAIL_GC_TIME_MS = 30 * 60 * 1000;
